import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { sipClient, type ClientConfig, type RegState } from './sip/SipClient';
import { history } from './lib/history';
import { loadPrefs, savePrefs, type Prefs } from './lib/settings';
import { fetchAccount, logout, type AccountState, type Me } from './lib/account';
import { dialString, formatNumber, isDialable } from './lib/phone';
import { tones } from './lib/tones';
import { Keypad } from './components/Keypad';
import { CallScreen } from './components/CallScreen';
import { Recents } from './components/Recents';
import { SettingsView } from './components/SettingsView';
import { Voicemails } from './components/Voicemails';
import { Contacts } from './components/Contacts';
import { SheetsProvider, useSheets } from './components/ContactSheets';
import { NgnLockup } from './components/NgnLockup';
import { contacts, useContacts, initials } from './lib/contacts';
import { useVoicemails } from './lib/voicemails';
import { BackspaceIcon, ClockIcon, GearIcon, GridIcon, LogoutIcon, PeopleIcon, PhoneIcon, VoicemailIcon } from './components/Icons';

type Tab = 'keypad' | 'recents' | 'contacts' | 'voicemail' | 'settings';
type SideTab = 'recents' | 'contacts' | 'voicemail';

/** FusionPBX feature code for the caller's own mailbox. */
const VOICEMAIL_CODE = '*97';

const REG_LABEL: Record<RegState, string> = {
  idle: 'Offline',
  connecting: 'Connecting',
  connected: 'Registering',
  registered: 'Online',
  failed: 'Error',
};

function useWide() {
  const q = '(min-width: 768px)';
  const [wide, setWide] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setWide(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return wide;
}

/** Ask for the microphone up-front so the browser prompt never appears mid-ring. */
async function primeMicrophone(): Promise<string | null> {
  try {
    const s = await navigator.mediaDevices.getUserMedia({ audio: true });
    s.getTracks().forEach((t) => t.stop());
    return null;
  } catch (e) {
    const name = (e as DOMException)?.name;
    if (name === 'NotAllowedError') return 'Microphone access is blocked — allow it in the address bar to make and take calls.';
    if (name === 'NotFoundError') return 'No microphone found.';
    return 'Could not access the microphone.';
  }
}

function clientConfig(me: Me, prefs: Prefs): ClientConfig {
  return {
    server: me.sip.server,
    domain: me.sip.domain,
    username: me.sip.username,
    ha1: me.sip.ha1,
    realm: me.sip.realm,
    displayName: me.sip.displayName,
    inputDeviceId: prefs.inputDeviceId,
    outputDeviceId: prefs.outputDeviceId,
    sipTrace: prefs.sipTrace,
  };
}

function Wordmark() {
  return (
    <picture className="wordmark">
      <source srcSet="/brand/logo-light.png" media="(prefers-color-scheme: light)" />
      <img src="/brand/logo-dark.png" alt="NGN" />
    </picture>
  );
}

function BootScreen({ children }: { children: React.ReactNode }) {
  return (
    <div className="boot">
      <div className="boot-card">
        <Wordmark />
        {children}
      </div>
    </div>
  );
}

export function App() {
  const [account, setAccount] = useState<AccountState>({ status: 'loading' });

  useEffect(() => {
    fetchAccount().then(setAccount);
  }, []);

  if (account.status === 'loading') {
    return (
      <BootScreen>
        <div className="spinner" aria-label="Loading" />
      </BootScreen>
    );
  }
  if (account.status === 'error') {
    return (
      <BootScreen>
        <p className="boot-msg">{account.message}</p>
        <button type="button" className="btn primary" onClick={() => window.location.reload()}>
          Try again
        </button>
      </BootScreen>
    );
  }
  if (account.status !== 'ready') {
    return (
      <BootScreen>
        <h1 className="boot-title">No phone line assigned</h1>
        <p className="boot-msg" data-testid="not-provisioned">
          You're signed in as <strong>{account.email}</strong>
          {account.status === 'extension_disabled' ? ', but your extension is currently disabled.' : ', but no extension has been assigned to you yet.'}
          <br />
          Contact NGN support to get set up.
        </p>
        <button type="button" className="btn" onClick={logout}>
          Sign out
        </button>
      </BootScreen>
    );
  }
  return <Phone me={account.me} />;
}

function Phone({ me }: { me: Me }) {
  const snap = useSyncExternalStore(sipClient.subscribe, sipClient.getSnapshot);
  const wide = useWide();
  const [prefs, setPrefs] = useState<Prefs>(loadPrefs);
  const [tab, setTab] = useState<Tab>('keypad');
  const [sideTab, setSideTab] = useState<SideTab>('recents');
  const [dial, setDial] = useState('');
  const [micError, setMicError] = useState<string | null>(null);
  const [callError, setCallError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const liveCalls = snap.calls;
  // MWI pushes (snap.voicemail) trigger an immediate refetch; the list itself is authoritative.
  const vm = useVoicemails(`${snap.voicemail?.new ?? '-'}/${snap.voicemail?.old ?? '-'}`);
  const vmNew = vm.counts?.new ?? snap.voicemail?.new ?? 0;
  const inCall = liveCalls.length > 0;

  const connect = useCallback(
    async (p: Prefs) => {
      tones.setSinkId(p.ringDeviceId);
      sipClient.connect(clientConfig(me, p));
      setMicError(await primeMicrophone());
      if ('Notification' in window && Notification.permission === 'default') Notification.requestPermission().catch(() => undefined);
    },
    [me],
  );

  // Register as soon as we know who the user is — completely transparent to them.
  useEffect(() => {
    void connect(prefs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connect]);

  const reloadVm = vm.reload;
  useEffect(
    () =>
      sipClient.onCallEnded(({ call }) => {
        history.add(call);
        if (call.number.startsWith('*9')) window.setTimeout(() => void reloadVm(), 1500); // voicemail codes
      }),
    [reloadVm],
  );

  // Browsers only allow audio after a gesture: unlock the tone engine on the first interaction.
  useEffect(() => {
    const unlock = () => tones.unlock();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  // Warn before closing mid-call; release the registration on exit; re-register from bfcache.
  useEffect(() => {
    const before = (e: BeforeUnloadEvent) => {
      if (sipClient.getSnapshot().calls.some((c) => c.state !== 'ended')) e.preventDefault();
    };
    const hide = () => sipClient.disconnect();
    const show = (e: PageTransitionEvent) => {
      if (e.persisted) sipClient.connect(clientConfig(me, loadPrefs()));
    };
    window.addEventListener('beforeunload', before);
    window.addEventListener('pagehide', hide);
    window.addEventListener('pageshow', show);
    return () => {
      window.removeEventListener('beforeunload', before);
      window.removeEventListener('pagehide', hide);
      window.removeEventListener('pageshow', show);
    };
  }, [me]);

  // Desktop notification + tab title for incoming calls.
  const incoming = liveCalls.find((c) => c.state === 'incoming');
  useEffect(() => {
    if (!incoming) {
      document.title = vmNew > 0 ? `(${vmNew}) NGN Phone` : 'NGN Phone';
      return;
    }
    const who = incoming.name || formatNumber(incoming.number);
    document.title = `Incoming call · ${who}`;
    let n: Notification | undefined;
    if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
      n = new Notification('Incoming call', { body: who, tag: incoming.id, requireInteraction: true, icon: '/brand/apps/phone/apple-touch-icon.png' });
      n.onclick = () => {
        window.focus();
        n?.close();
      };
    }
    return () => n?.close();
  }, [incoming?.id, vmNew]); // eslint-disable-line react-hooks/exhaustive-deps

  const placeCall = async (target = dial) => {
    setCallError('');
    if (!isDialable(target)) return;
    if (snap.reg !== 'registered') {
      setCallError('Not connected to the phone system yet.');
      return;
    }
    try {
      await sipClient.call(target);
      setDial('');
      setTab('keypad');
    } catch (e) {
      const name = (e as DOMException)?.name;
      setCallError(name === 'NotAllowedError' ? 'Microphone access is blocked.' : ((e as Error).message ?? 'Call failed'));
    }
  };

  const onKey = (d: string) => {
    tones.dtmf(d);
    setDial((s) => s + d);
    inputRef.current?.focus();
  };

  const onSavePrefs = (p: Prefs) => {
    const micChanged = p.inputDeviceId !== prefs.inputDeviceId;
    savePrefs(p);
    setPrefs(p);
    tones.setSinkId(p.ringDeviceId);
    sipClient.setOutputDevice(p.outputDeviceId);
    if (micChanged) void sipClient.setInputDevice(p.inputDeviceId);
    setTab('keypad');
  };

  // Numbers from contacts / history / voicemail are stored canonically; dial them the way the
  // PBX's outbound routes like best (10-digit national for AU). Keypad input is dialled as typed.
  const callStored = (n: string) => void placeCall(dialString(n));

  const stage = inCall ? (
    <CallScreen calls={liveCalls} />
  ) : tab === 'settings' ? (
    <SettingsView prefs={prefs} me={me} onSave={onSavePrefs} onCancel={() => setTab('keypad')} />
  ) : !wide && tab !== 'keypad' ? (
    <SideContent tab={tab} vm={vm} onCall={callStored} onListen={() => void placeCall(VOICEMAIL_CODE)} />
  ) : (
    <Dialer
      dial={dial}
      setDial={setDial}
      inputRef={inputRef}
      onKey={onKey}
      onCall={() => void placeCall()}
      onVoicemail={() => void placeCall(VOICEMAIL_CODE)}
      registered={snap.reg === 'registered'}
      vmNew={vmNew}
      callError={callError}
    />
  );

  const badge = vmNew > 0 && (
    <span className="badge inline" data-testid="vm-tab-badge">
      {vmNew}
    </span>
  );

  const regPill = (
    <div className={`reg reg-${snap.reg}`} data-testid="reg-state" data-state={snap.reg} title={snap.regError ?? REG_LABEL[snap.reg]}>
      <span className="dot" />
      <span className="reg-label">{REG_LABEL[snap.reg]}</span>
    </div>
  );
  const identity = (
    <div className="identity" data-testid="identity">
      <strong>
        {me.user.name} - {me.line.extension}
      </strong>
      {me.line.callerIdDisplay && <span data-testid="caller-id">{me.line.callerIdDisplay}</span>}
    </div>
  );
  const banner = (snap.regError || micError) && (
    <div className="banner" role="alert">
      {snap.regError ?? micError}
    </div>
  );

  return (
    <SheetsProvider onCall={callStored}>
      <div className={`shell ${wide ? 'split' : 'compact'}`}>
        {wide && (
          <aside className="sidebar">
            <div className="sidebar-head">
              <div className="brand">
                <NgnLockup app="phone" href="/" />
              </div>
              <div className="head-actions">
                <button
                  type="button"
                  className={`icon-btn ${tab === 'settings' ? 'on' : ''}`}
                  aria-label="Settings"
                  title="Settings"
                  data-testid="tab-settings"
                  onClick={() => setTab(tab === 'settings' ? 'keypad' : 'settings')}
                >
                  <GearIcon />
                </button>
                <button type="button" className="icon-btn" aria-label="Sign out" title="Sign out" onClick={logout}>
                  <LogoutIcon />
                </button>
              </div>
            </div>
            <div className="me">
              <div className="me-avatar">{initials(me.user.name)}</div>
              <div className="me-text">
                {identity}
                {regPill}
              </div>
            </div>
            <div className="segmented" role="tablist">
              {(['recents', 'contacts', 'voicemail'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={sideTab === t}
                  className={sideTab === t ? 'on' : ''}
                  onClick={() => setSideTab(t)}
                  data-testid={`side-${t}`}
                >
                  {t === 'recents' ? 'Recents' : t === 'contacts' ? 'Contacts' : 'Voicemail'}
                  {t === 'voicemail' && badge}
                </button>
              ))}
            </div>
            <div className="scroll">
              <SideContent tab={sideTab} vm={vm} onCall={callStored} onListen={() => void placeCall(VOICEMAIL_CODE)} />
            </div>
          </aside>
        )}

        <div className="main">
          {!wide && !inCall && (
            <header className="topbar">
              <NgnLockup app="phone" href="/" />
              <div className="topbar-right">
                {identity}
                {regPill}
              </div>
            </header>
          )}
          {banner}
          <main className="stage">{stage}</main>
          {!wide && !inCall && (
            <nav className="tabbar">
              {(
                [
                  ['recents', 'Recents', <ClockIcon key="i" />],
                  ['contacts', 'Contacts', <PeopleIcon key="i" />],
                  ['keypad', 'Keypad', <GridIcon key="i" />],
                  ['voicemail', 'Voicemail', <VoicemailIcon key="i" />],
                  ['settings', 'Settings', <GearIcon key="i" />],
                ] as const
              ).map(([t, label, icon]) => (
                <button key={t} type="button" className={tab === t ? 'on' : ''} onClick={() => setTab(t)} data-testid={`tab-${t}`}>
                  <span className="tab-icon">
                    {icon}
                    {t === 'voicemail' && badge}
                  </span>
                  {label}
                </button>
              ))}
            </nav>
          )}
        </div>
      </div>
    </SheetsProvider>
  );
}

function SideContent({
  tab,
  vm,
  onCall,
  onListen,
}: {
  tab: SideTab;
  vm: ReturnType<typeof useVoicemails>;
  onCall: (n: string) => void;
  onListen: () => void;
}) {
  if (tab === 'contacts') return <Contacts />;
  if (tab === 'voicemail') return <Voicemails messages={vm.messages} counts={vm.counts} error={vm.error} onListen={onListen} onCall={onCall} />;
  return <Recents onCall={onCall} />;
}

function Dialer(props: {
  dial: string;
  setDial: (fn: (s: string) => string) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onKey: (d: string) => void;
  onCall: () => void;
  onVoicemail: () => void;
  registered: boolean;
  vmNew: number;
  callError: string;
}) {
  const { dial, setDial } = props;
  useContacts();
  const { openEditor, openDetail } = useSheets();
  const known = dial.replace(/\D/g, '').length >= 6 ? contacts.lookup(dial) : undefined;
  const canAdd = !known && /^\+?\d{6,}$/.test(dial.replace(/[\s\-().]/g, ''));
  return (
    <form
      className="dialer"
      onSubmit={(e) => {
        e.preventDefault();
        props.onCall();
      }}
    >
      <div className="dial-display">
        <input
          ref={props.inputRef}
          autoFocus
          inputMode="tel"
          aria-label="Number to call"
          placeholder="Enter a number"
          data-testid="dial-input"
          value={dial}
          onChange={(e) => {
            const v = e.target.value;
            // Typed / pasted digits: play the key tone for the last character added.
            if (v.length === dial.length + 1 && /[0-9*#]$/.test(v)) tones.dtmf(v.slice(-1));
            setDial(() => v);
          }}
        />
        {known ? (
          <button type="button" className="dial-hint" onClick={() => openDetail(known)}>
            {known.name}
          </button>
        ) : canAdd ? (
          <button type="button" className="dial-hint" data-testid="dial-add-contact" onClick={() => openEditor({ number: dial })}>
            Add to Contacts
          </button>
        ) : (
          <span className="dial-hint" />
        )}
      </div>
      <Keypad onDigit={props.onKey} />
      <p className="error center" role="status" style={{ minHeight: 18 }}>
        {props.callError}
      </p>
      <div className="action-row">
        <button
          type="button"
          className="round side"
          aria-label={props.vmNew ? `Voicemail, ${props.vmNew} new` : 'Voicemail'}
          title="Voicemail"
          data-testid="voicemail-button"
          disabled={!props.registered}
          onClick={props.onVoicemail}
        >
          <VoicemailIcon />
          {props.vmNew > 0 && (
            <span className="badge" data-testid="vm-badge">
              {props.vmNew}
            </span>
          )}
        </button>
        <button type="submit" className="round big green" aria-label="Call" data-testid="call-button" disabled={!isDialable(dial) || !props.registered}>
          <PhoneIcon />
        </button>
        <button
          type="button"
          className="round side"
          aria-label="Delete digit"
          style={{ visibility: dial ? 'visible' : 'hidden' }}
          onClick={() => setDial((s) => s.slice(0, -1))}
        >
          <BackspaceIcon />
        </button>
      </div>
    </form>
  );
}
